import React, { useCallback, useEffect, useState } from "react";
import GaussianSplatMedia from "./GaussianSplatMedia";
import ProjectLightbox from "./ProjectLightbox";

const DEFAULT_PROJECT_BG =
  "linear-gradient(145deg, color-mix(in srgb, var(--accent) 18%, var(--card)), var(--surface-2)), linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)";

const imageSource = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.src || "";
};

const mediaType = (media) => media?.type || "image";

const TabButton = ({ active, children, controls, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    aria-controls={controls}
    onClick={onClick}
    className={`flex-1 px-3 py-3.5 font-mono text-[9px] uppercase tracking-[0.12em] transition sm:px-6 ${
      active
        ? "border-b-2 border-accent bg-surface2 text-textmain"
        : "text-muted hover:text-textmain"
    }`}
  >
    {children}
  </button>
);

const ProjectCover = ({ project }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const coverSource = imageSource(project.coverImage);
  const coverType = mediaType(project.coverImage);
  const showMedia = Boolean(coverSource) && (coverType === "gsplat" || !imageFailed);

  useEffect(() => {
    setImageFailed(false);
  }, [coverSource]);

  return (
    <div
      className="aspect-video overflow-hidden rounded-[var(--radius-md)] border border-line bg-surface2"
      style={{ background: DEFAULT_PROJECT_BG, backgroundSize: "auto, 24px 24px, 24px 24px" }}
    >
      <div className="flex h-7 items-center gap-1.5 border-b border-line bg-surface/80 px-3">
        <span className="h-2 w-2 rounded-full bg-red-400/70" />
        <span className="h-2 w-2 rounded-full bg-amber-400/70" />
        <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
      </div>
      <div className="h-[calc(100%-1.75rem)]">
        {showMedia && coverType === "gsplat" ? (
          <GaussianSplatMedia asset={project.coverImage} fullScreen={false} />
        ) : showMedia ? (
          <img
            src={coverSource}
            alt={project.coverImage?.alt || `${project.title} cover`}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="flex h-full min-w-0">
            <div className="w-[18%] border-r border-line bg-surface/50 p-3">
              <span className="block h-2 w-full rounded bg-accent/30" />
              <span className="mt-2 block h-2 w-3/4 rounded bg-line" />
              <span className="mt-2 block h-2 w-5/6 rounded bg-line" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-between p-4">
              <div className="rounded-lg border border-line bg-surface/60 p-3">
                <span className="block h-2 w-2/3 rounded bg-accent2/40" />
                <span className="mt-2 block h-2 w-full rounded bg-line" />
              </div>
              <div className="flex h-1/2 items-end gap-2 px-2">
                {[45, 70, 55, 82, 62].map((height) => (
                  <span
                    key={height}
                    className="flex-1 rounded-t bg-accent/35"
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
              <span className="truncate text-center text-xs font-mono text-muted">
                {project.coverText || project.title}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectLinks = ({ links }) => {
  if (!links?.source && !links?.live) return null;

  return (
    <div className="flex flex-wrap gap-4">
      {links.source && (
        <a
          href={links.source}
          target="_blank"
          rel="noreferrer"
          className="border-b border-accent text-sm font-mono text-textmain transition hover:text-accent"
        >
          Source code →
        </a>
      )}
      {links.live && (
        <a
          href={links.live}
          target="_blank"
          rel="noreferrer"
          className="border-b border-accent text-sm font-mono text-textmain transition hover:text-accent"
        >
          Live demo →
        </a>
      )}
    </div>
  );
};

const StackGroup = ({ label, values }) => {
  if (!values?.length) return null;

  return (
    <div>
      <p className="mb-2 text-xs font-mono text-accent2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {values.map((technology) => (
          <span
            key={technology}
          className="rounded-[4px] border border-line bg-surface2 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[.06em] text-textmain"
          >
            {technology}
          </span>
        ))}
      </div>
    </div>
  );
};

const stackGroupLabel = (label) =>
  label
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/^./, (character) => character.toUpperCase());

const ProjectCard = ({ project }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const images = project.images || [];
  const hasImages = images.length > 0;
  const panelId = `project-${project.id}-${activeTab}`;

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const showNextImage = useCallback(() => {
    setLightboxIndex((index) => (index + 1) % images.length);
  }, [images.length]);
  const showPreviousImage = useCallback(() => {
    setLightboxIndex((index) =>
      index === 0 ? images.length - 1 : index - 1
    );
  }, [images.length]);

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <article className="grid w-full grid-cols-1 overflow-hidden rounded-[var(--radius-lg)] border border-line bg-card shadow-soft">
      <div
        className="invisible col-start-1 row-start-1 aspect-video w-full"
        aria-hidden="true"
      />

      <div className="col-start-1 row-start-1 w-full min-w-0">
        <div className="flex border-b border-line" role="tablist">
          <TabButton
            active={activeTab === "overview"}
            controls={`project-${project.id}-overview`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </TabButton>
          {hasImages && (
            <TabButton
              active={activeTab === "shots"}
              controls={`project-${project.id}-shots`}
              onClick={() => setActiveTab("shots")}
            >
              Media
            </TabButton>
          )}
          <TabButton
            active={activeTab === "tech"}
            controls={`project-${project.id}-tech`}
            onClick={() => setActiveTab("tech")}
          >
            Stack
          </TabButton>
        </div>

        {activeTab === "overview" && (
          <div
            id={panelId}
            role="tabpanel"
            className="p-5 sm:p-6 md:p-8"
          >
            <div className="grid min-w-0 gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
              <div className="min-w-0">
                <ProjectCover project={project} />
                <div className="my-5">
                  <ProjectLinks links={project.links} />
                </div>
              </div>

              <div className="flex min-w-0 flex-col gap-4">
                <div>
                  <div className="mb-2 flex flex-col items-start justify-between gap-3 sm:flex-row">
                    <h2 className="min-w-0 break-words font-display text-3xl font-semibold tracking-[-0.035em] text-textmain">
                      {project.title}
                    </h2>
                    {project.status && (
                      <span className="shrink-0 rounded-[4px] border border-accent px-2.5 py-1 font-mono text-[9px] uppercase tracking-[.08em] text-accent">
                        ● {project.status.split("•")[0].trim()}
                      </span>
                    )}
                  </div>
                  {project.tagline && (
                    <p className="mb-2 text-sm text-muted">{project.tagline}</p>
                  )}
                </div>

                <p className="text-sm leading-relaxed text-muted">
                  {project.description}
                </p>
              </div>
            </div>
            {project.metrics?.length > 0 && (
              <div className="ml-6 pt-2">
                <p className="mb-3 text-xs font-mono uppercase tracking-wide text-accent2">
                  Key Metrics
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {project.metrics.map((metric) => (
                    <div
                      key={metric.label}
                      className="border-l border-accent bg-surface2 p-4"
                    >
                      <div className="mb-1 text-lg font-bold text-accent2">
                        {metric.value}
                      </div>
                      <div className="text-xs text-muted">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {project.highlight && (
              <div className="mt-8 rounded-[var(--radius-md)] border border-line bg-surface2 p-5">
                <p className="mb-2 text-xs font-mono uppercase tracking-wide text-accent2">
                  {project.highlight.title}
                </p>
                <p className="text-sm leading-relaxed text-muted">
                  {project.highlight.text}
                </p>
              </div>
            )}
            {project.challenges?.length > 0 && (
              <div className="mt-8">
                <p className="mb-4 text-xs font-mono uppercase tracking-wide text-accent2">
                  Challenges
                </p>
                <ul className="space-y-3">
                  {project.challenges.map((challenge) => (
                    <li key={challenge} className="flex gap-3 text-sm text-muted">
                      <span className="shrink-0 text-accent2">—</span>
                      <span>{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {project.features?.length > 0 && (
              <div className="mt-8">
                <p className="mb-4 text-xs font-mono uppercase tracking-wide text-accent2">
                  Key Features
                </p>
                <ul className="space-y-3">
                  {project.features.map((feature) => (
                    <li key={feature.title} className="flex gap-3">
                      <span className="mt-1 shrink-0 text-accent">—</span>
                      <div>
                        <strong className="block text-sm text-textmain">
                          {feature.title}
                        </strong>
                        <p className="mt-0.5 text-sm text-muted">
                          {feature.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {hasImages && activeTab === "shots" && (
          <div
            id={panelId}
            role="tabpanel"
            className="p-5 sm:p-6 md:p-8"
          >
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
              {images.map((media, index) => (
                <div
                  key={`${media.src}-${index}`}
                  className="group relative mb-4 w-full break-inside-avoid overflow-hidden rounded-[var(--radius-md)] border border-line text-left transition hover:border-accent"
                >
                  <div className="relative aspect-video overflow-hidden bg-surface2">
                    {mediaType(media) === "gsplat" ? (
                      <GaussianSplatMedia asset={media} fullScreen={false} />
                    ) : (
                      <img
                        src={media.src}
                        alt={media.alt || "Project screenshot"}
                        className="h-full w-full object-cover transition group-hover:scale-105"
                        loading="lazy"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => handleImageClick(index)}
                      className="absolute right-2 top-2 z-10 rounded-full border border-white/25 bg-black/65 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[.08em] text-white backdrop-blur transition hover:bg-black/85"
                      aria-label={`Open ${media.alt || "project media"} fullscreen`}
                    >
                      Fullscreen ↗
                    </button>
                  </div>
                  {media.caption && (
                    <p className="p-2 text-xs font-mono text-muted">
                      {media.caption}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "tech" && (
          <div
            id={panelId}
            role="tabpanel"
            className="px-5 pb-5 pt-8 sm:px-6 sm:pb-6 sm:pt-10 md:px-8 md:pb-8 md:pt-12"
          >
            <div className="mb-8">
              <p className="mb-3 text-xs font-mono uppercase tracking-wide text-accent2">
                Tech Stack
              </p>
              <div className="grid gap-6 sm:grid-cols-2">
                {Object.entries(project.stack || {}).map(([groupName, technologies]) => (
                  <StackGroup
                    key={groupName}
                    label={stackGroupLabel(groupName)}
                    values={technologies}
                  />
                ))}
              </div>
            </div>

            {(project.role || project.timeline) && (
              <div className="grid gap-4 sm:grid-cols-2">
                {project.role && (
                  <div>
                    <p className="mb-2 text-xs font-mono uppercase tracking-wide text-accent2">
                      Role
                    </p>
                    <p className="text-sm text-textmain">{project.role}</p>
                  </div>
                )}
                {project.timeline && (
                  <div>
                    <p className="mb-2 text-xs font-mono uppercase tracking-wide text-accent2">
                      Timeline
                    </p>
                    <p className="text-sm text-textmain">{project.timeline}</p>
                  </div>
                )}
              </div>
            )}

            {(project.links?.source || project.links?.live) && (
              <div className="mt-8 border-t border-line pt-6">
                <ProjectLinks links={project.links} />
              </div>
            )}
          </div>
        )}
      </div>

      {hasImages && (
        <ProjectLightbox
          images={images}
          currentIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={closeLightbox}
          onNext={showNextImage}
          onPrev={showPreviousImage}
        />
      )}
    </article>
  );
};

export default ProjectCard;
